import {
  CUSTOMER_STATUS_LABELS,
  CUSTOMER_STATUSES,
  type CustomerProfile,
  customerStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  Input,
  Select,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Contact, Plus } from "lucide-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { customerListQuery, isSearchable } from "../../../features/customers/customer-values.js";
import { MaskedNotice, PersonalValue } from "../../../features/customers/personal-data.js";
import { formatPhoneDisplay } from "../../../features/customers/personal-data-values.js";
import {
  useCustomerList,
  useCustomerPermissions,
  useCustomerStations,
} from "../../../features/customers/queries.js";
import { CustomersPage, CustomerStatusBadge, DriverLinkBadge } from "../../../features/customers/shared.js";
import { compactSearch } from "../../../features/shifts/shift-values.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

const searchSchema = z.object({
  q: z.string().max(100).optional().catch(undefined),
  status: customerStatusSchema.optional().catch(undefined),
  station: z.string().uuid().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/musteriler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: CustomersRoute,
});

/**
 * Müşteri listesi (Prompt 17 web): ad, telefon, kart no, kod ya da plakayla arama; durum ve ana istasyon süzgeci.
 * Kişisel veri `customers.personal.view` yoksa sunucuda MASKELENİR: sayfa başında açıklama, hücrede "maskeli" rozeti.
 */
function CustomersRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useCustomerPermissions();
  return (
    <CustomersPage
      title={t("menu.customers")}
      description={t("customers.list.description")}
      actions={
        permissions.canManage ? (
          <Button asChild>
            <Link to="/musteriler/yeni">
              <Plus aria-hidden="true" />
              {t("customers.list.new")}
            </Link>
          </Button>
        ) : null
      }
    >
      <CustomerList />
    </CustomersPage>
  );
}

function CustomerList(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const shape = useTenantShape();
  const stations = useCustomerStations();
  const [searchText, setSearchText] = useState(search.q ?? "");
  const filters = { q: search.q, status: search.status, homeStationId: search.station };
  const pages = useCursorPages(JSON.stringify(customerListQuery(filters, undefined)));
  const list = useCustomerList(filters, pages.cursor, true);
  const masked = (list.data?.items ?? []).some((row) => row.personalDataMasked);
  const tooShort = searchText.trim().length > 0 && !isSearchable(searchText);

  const setFilters = (patch: Record<string, unknown>) =>
    void navigate({
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  // Arama metni URL'ye yazılmadan önce beklenir; 1 karakterlik metin API'ye GİTMEZ (sunucu en az 2 ister).
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = isSearchable(searchText) ? searchText.trim() : undefined;
      if ((search.q ?? undefined) !== next) setFilters({ q: next });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const columns = useMemo<DataTableColumn<CustomerProfile>[]>(
    () => [
      {
        id: "code",
        header: t("customers.fields.code"),
        hideable: false,
        exportValue: (row) => row.code,
        cell: (row) => (
          <Link
            to="/musteriler/$id"
            params={{ id: row.id }}
            className="font-mono font-medium whitespace-nowrap text-primary underline underline-offset-4"
          >
            {row.code}
          </Link>
        ),
      },
      {
        id: "fullName",
        header: t("customers.fields.fullName"),
        hideable: false,
        exportValue: (row) => row.fullName,
        cell: (row) => (
          <PersonalValue value={row.fullName} masked={row.personalDataMasked} className="min-w-32" />
        ),
      },
      {
        id: "phone",
        header: t("customers.fields.phone"),
        exportValue: (row) => row.phone,
        cell: (row) => (
          <PersonalValue
            value={row.phone}
            masked={row.personalDataMasked}
            format={formatPhoneDisplay}
            className="whitespace-nowrap tabular"
          />
        ),
      },
      {
        id: "email",
        header: t("customers.fields.email"),
        exportValue: (row) => row.email,
        cell: (row) => <PersonalValue value={row.email} masked={row.personalDataMasked} />,
      },
      {
        id: "cardNo",
        header: t("customers.fields.cardNo"),
        exportValue: (row) => row.loyaltyCardNo,
        cell: (row) =>
          row.loyaltyCardNo ? (
            <span className="font-mono whitespace-nowrap">{row.loyaltyCardNo}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "homeStation",
        header: t("customers.fields.homeStation"),
        exportValue: (row) => row.homeStationName,
        cell: (row) => row.homeStationName ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: "counterparty",
        header: t("customers.fields.counterparty"),
        defaultVisible: false,
        exportValue: (row) => row.counterpartyName,
        cell: (row) => row.counterpartyName ?? <span className="text-muted-foreground">—</span>,
      },
      {
        id: "driverLinked",
        header: t("customers.fields.driverApp"),
        exportValue: (row) =>
          row.driverLinked ? t("customers.driver.linked") : t("customers.driver.notLinked"),
        cell: (row) => <DriverLinkBadge linked={row.driverLinked} />,
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => CUSTOMER_STATUS_LABELS[row.status],
        cell: (row) => <CustomerStatusBadge status={row.status} />,
      },
    ],
    [t],
  );

  const hasFilters = Boolean(search.q || search.status || search.station);

  return (
    <>
      <MaskedNotice masked={masked} />
      <DataTable
        caption={t("customers.list.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Contact />}
            title={hasFilters ? t("customers.list.emptyFilteredTitle") : t("customers.list.emptyTitle")}
            description={
              hasFilters ? t("customers.list.emptyFilteredDescription") : t("customers.list.emptyDescription")
            }
          />
        }
        filters={
          <>
            <FormField
              label={t("common.search")}
              className="w-72 max-w-full"
              description={tooShort ? t("customers.list.searchHint") : undefined}
            >
              {(control) => (
                <Input
                  {...control}
                  type="search"
                  value={searchText}
                  maxLength={100}
                  onChange={(event) => setSearchText(event.target.value)}
                  placeholder={t("customers.list.searchPlaceholder")}
                />
              )}
            </FormField>
            <FormField label={t("common.status")} className="w-44">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) => setFilters({ status: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {CUSTOMER_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {CUSTOMER_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            {!shape.isSingleStation && (stations.data?.length ?? 0) > 1 ? (
              <FormField label={t("customers.fields.homeStation")} className="w-60 max-w-full">
                {(control) => (
                  <Select
                    {...control}
                    value={search.station ?? ""}
                    onChange={(event) => setFilters({ station: event.target.value || undefined })}
                  >
                    <option value="">{t("common.all")}</option>
                    {(stations.data ?? []).map((station) => (
                      <option key={station.id} value={station.id}>
                        {station.code} {station.name}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            ) : null}
            {hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                className="mb-1"
                onClick={() => {
                  setSearchText("");
                  void navigate({ search: {}, replace: true });
                }}
              >
                {t("common.clearFilters")}
              </Button>
            ) : null}
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<CustomerProfile>(
            async (cursor) =>
              (await api.GET("/customers", { params: { query: customerListQuery(filters, cursor, 200) } }))
                .data!,
          );
          await exportRowsToExcel({
            fileName: "musteriler",
            sheetName: t("menu.customers"),
            columns: visible,
            rows,
          });
        }}
      />
    </>
  );
}
